
import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { useGameMode, GameMode } from './GameModeContext';
import { WordSelectionStrategy } from '@/lib/wordSelection/WordSelectionStrategy';
import { DailyWordStrategy } from '@/lib/wordSelection/DailyWordStrategy';
import { PracticeWordStrategy } from '@/lib/wordSelection/PracticeWordStrategy';

interface LevelConfig {
  wordLength: number;
  maxAttempts: number;
}

export interface GameConfig {
  wordSelectionStrategy: WordSelectionStrategy;
  levels: LevelConfig[];
  maxLevels: number;
  useStreak: boolean;
  showDailyCountdown: boolean;
  saveGameState: boolean;
  storageAdapter: any; // Will replace with proper type later
}

interface GameConfigContextType {
  config: GameConfig;
}

const defaultConfig: GameConfig = {
  wordSelectionStrategy: new DailyWordStrategy(),
  levels: [
    { wordLength: 3, maxAttempts: 6 },
    { wordLength: 4, maxAttempts: 5 },
    { wordLength: 5, maxAttempts: 4 },
    { wordLength: 6, maxAttempts: 3 },
  ],
  maxLevels: 4,
  useStreak: true,
  showDailyCountdown: true,
  saveGameState: true,
  storageAdapter: null, // Will be replaced during initialization
};

// Define configs for each game mode with factory functions to ensure new instances
const getModeConfig = (mode: GameMode, baseConfig: GameConfig): GameConfig => {
  switch(mode) {
    case 'daily':
      return { 
        ...baseConfig, 
        useStreak: true, 
        showDailyCountdown: true,
        wordSelectionStrategy: new DailyWordStrategy() // Create new instance
      };
      
    case 'practice':
      return { 
        ...baseConfig, 
        useStreak: false, 
        showDailyCountdown: false,
        wordSelectionStrategy: new PracticeWordStrategy() // Create new instance
      };
      
    case 'custom':
      return { 
        ...baseConfig, 
        useStreak: false, 
        showDailyCountdown: false
      };
      
    default:
      return { ...baseConfig };
  }
};

export const GameConfigContext = createContext<GameConfigContextType>({
  config: defaultConfig,
});

export const GameConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentMode, getModeConfig: getModeMeta } = useGameMode();
  const modeConfig = getModeMeta(currentMode);
  
  // Use useMemo to create a new config whenever the mode changes
  const config = useMemo(() => {
    // Create a deep clone of default config to avoid shared references
    const baseConfig = JSON.parse(JSON.stringify({...defaultConfig}));
    
    // Apply mode-specific configuration using the factory function
    const newConfig = getModeConfig(currentMode, baseConfig);
    
    // Set the storage adapter based on the current mode
    newConfig.storageAdapter = { storagePrefix: modeConfig.storagePrefix };
    
    console.log(`Created new game config for mode: ${currentMode}`, {
      useStreak: newConfig.useStreak,
      showDailyCountdown: newConfig.showDailyCountdown,
      strategyType: newConfig.wordSelectionStrategy.constructor.name,
      strategyId: newConfig.wordSelectionStrategy.getStrategyId(),
      timestamp: new Date().toISOString()
    });
    
    return newConfig;
  }, [currentMode, modeConfig]);

  return (
    <GameConfigContext.Provider value={{ config }}>
      {children}
    </GameConfigContext.Provider>
  );
};

export const useGameConfig = () => useContext(GameConfigContext);

// Helper functions for common game configuration needs
export const getMaxAttemptsForLevel = (level: number): number => {
  const { config } = useGameConfig();
  // Ensure level is within bounds
  const safeLevel = Math.max(1, Math.min(level, config.maxLevels));
  return config.levels[safeLevel - 1]?.maxAttempts || 6; // Default to 6 if not found
};

export const getWordLengthForLevel = (level: number): number => {
  const { config } = useGameConfig();
  // Ensure level is within bounds
  const safeLevel = Math.max(1, Math.min(level, config.maxLevels));
  return config.levels[safeLevel - 1]?.wordLength || 3; // Default to 3 if not found
};
